import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Dialog, IconButton, Typography, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BlogPost } from '../../types/blog-generation-agent.types';
import Markdown from 'markdown-to-jsx';
import { posthogService, PostHogEvent } from 'src/common/services/posthogService';

const stripMarkdown = (markdown: string): string => {
  if (!markdown) return '';

  let text = markdown.replace(/\r\n/g, '\n');

  text = text
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove code blocks
    .replace(/```[a-z]*\n([\s\S]*?)\n```/g, '$1')
    // Remove inline code block formatting
    .replace(/`([^`]+)`/g, '$1')
    // Replace link markdown [text](url) with just text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove images: ![alt](url) -> ''
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    // Remove bold and italic markers: **text**, *text*, __text__, _text_
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Remove headers: #, ##, ###, etc.
    .replace(/^\s*#+\s+(.*)$/gm, '$1')
    // Remove blockquotes: > text -> text
    .replace(/^\s*>\s+(.*)$/gm, '$1')
    // Remove horizontal rules: --- or *** or ___
    .replace(/^\s*[-*_]{3,}\s*$/gm, '')
    // Convert bullet lists (* item or - item or + item) to plain text with bullet (• item)
    .replace(/^\s*[-*+]\s+(.*)$/gm, '• $1');

  return text.trim();
};

interface ViewBlogPostDialogProps {
  open: boolean;
  onClose: () => void;
  post: BlogPost | null;
  isLoading?: boolean;
}

export const ViewBlogPostDialog: React.FC<ViewBlogPostDialogProps> = ({
  open,
  onClose,
  post,
  isLoading = false
}) => {
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  // Track Orbit AI Blog Post modal opened in PostHog
  useEffect(() => {
    if (open && post) {
      posthogService.track(PostHogEvent.ORBIT_AI_BLOG_VIEWED, {
        post_title: post?.title
      });
    }
  }, [open, post]);

  const handleCopyMarkdown = () => {
    if (post?.content_markdown) {
      navigator.clipboard.writeText(post.content_markdown);
      setCopiedMarkdown(true);
      setTimeout(() => setCopiedMarkdown(false), 2000);

      // Track Orbit AI Blog Markdown copied to clipboard in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_BLOG_COPIED, {
        format: 'markdown',
        post_title: post?.title
      });
    }
  };

  const handleCopyText = () => {
    if (post?.content_markdown) {
      navigator.clipboard.writeText(stripMarkdown(post.content_markdown));
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);

      // Track Orbit AI Blog plain text copied to clipboard in PostHog
      posthogService.track(PostHogEvent.ORBIT_AI_BLOG_COPIED, {
        format: 'text',
        post_title: post?.title
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} disableScrollLock maxWidth="md" fullWidth>
      <div className="p-6 relative flex flex-col max-h-[85vh]">
        {/* Title */}
        <p className="text-2xl font-bold text-primary mb-4 pr-8">
          Blog Post
        </p>

        {/* Top-right close button */}
        <div aria-label="dialog-close-btn" className="absolute top-4 right-4">
          <IconButton
            aria-label="close"
            onClick={onClose}
            className="hover:rounded-lg"
            sx={{
              height: '40px',
              padding: '8px',
            }}
          >
            <CloseIcon sx={{ color: '#000000', fontSize: '24px' }} />
          </IconButton>
        </div>

        {/* Scrollable Container */}
        <div className="flex-1 overflow-y-auto pr-2 mb-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <CircularProgress size={36} sx={{ color: '#0F0A3C' }} />
            </div>
          ) : !post ? (
            <div className="flex items-center justify-center py-20">
              <p className="text-[#667085] text-sm">No blog post data available.</p>
            </div>
          ) : (
            <>
              {/* Post Title */}
              <h3 className="text-base font-medium text-[#475467] mb-3">
                {post.title}
              </h3>

              {/* Keyword Labels / Pills */}
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {post.primary_keywords?.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-medium px-2 py-0.5 rounded-2xl bg-[#ECFDF3] text-[#1A927B]"
                  >
                    {keyword}
                  </span>
                ))}
                {post.secondary_keywords?.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-medium px-2 py-0.5 rounded-2xl bg-[#F5F5F5] text-[#414651]"
                  >
                    {keyword}
                  </span>
                ))}
              </div>

              {/* Blog Content */}
              <div className="w-full">
                <Markdown
                  options={{
                    overrides: {
                      h1: {
                        component: 'h1',
                        props: {
                          style: {
                            fontSize: '1.75em',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: '#24292e',
                            marginTop: '24px',
                            marginBottom: '16px',
                            paddingBottom: '0.3em',
                            borderBottom: '1px solid #eaecef'
                          }
                        }
                      },
                      h2: {
                        component: 'h2',
                        props: {
                          style: {
                            fontSize: '1.375em',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: '#24292e',
                            marginTop: '24px',
                            marginBottom: '16px',
                            paddingBottom: '0.3em',
                            borderBottom: '1px solid #eaecef'
                          }
                        }
                      },
                      h3: {
                        component: 'h3',
                        props: {
                          style: {
                            fontSize: '1.125em',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: '#24292e',
                            marginTop: '24px',
                            marginBottom: '16px'
                          }
                        }
                      },
                      h4: {
                        component: 'h4',
                        props: {
                          style: {
                            fontSize: '1em',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: '#24292e',
                            marginTop: '24px',
                            marginBottom: '16px'
                          }
                        }
                      },
                      h5: {
                        component: 'h5',
                        props: {
                          style: {
                            fontSize: '0.875em',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: '#24292e',
                            marginTop: '24px',
                            marginBottom: '16px'
                          }
                        }
                      },
                      h6: {
                        component: 'h6',
                        props: {
                          style: {
                            fontSize: '0.85em',
                            fontWeight: 600,
                            lineHeight: 1.25,
                            color: '#6a737d',
                            marginTop: '24px',
                            marginBottom: '16px'
                          }
                        }
                      },
                      p: {
                        component: 'p',
                        props: {
                          style: {
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: 1.6,
                            color: '#24292e',
                            marginTop: '0',
                            marginBottom: '16px',
                          }
                        }
                      },
                      a: {
                        component: Link,
                        props: {
                          target: '_blank',
                          rel: 'noopener noreferrer',
                          sx: {
                            color: '#0366d6',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            },
                          }
                        }
                      },
                      input: {
                        component: 'input',
                        props: {
                          style: {
                            display: 'none'
                          }
                        }
                      },
                      hr: {
                        component: 'hr',
                        props: {
                          style: {
                            height: '0.25em',
                            padding: 0,
                            margin: '24px 0',
                            backgroundColor: '#e1e4e8',
                            border: 'none'
                          }
                        }
                      },
                      ul: {
                        component: 'ul',
                        props: {
                          style: {
                            listStyleType: 'disc',
                            marginTop: 0,
                            marginBottom: '16px',
                            paddingLeft: '2em',
                            color: '#24292e'
                          }
                        }
                      },
                      ol: {
                        component: 'ol',
                        props: {
                          style: {
                            listStyleType: 'decimal',
                            marginTop: 0,
                            marginBottom: '16px',
                            paddingLeft: '2em',
                            color: '#24292e'
                          }
                        }
                      },
                      li: {
                        component: 'li',
                        props: {
                          style: {
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: 1.6,
                            marginTop: '0.25em'
                          }
                        }
                      },
                      blockquote: {
                        component: 'blockquote',
                        props: {
                          style: {
                            padding: '0 1em',
                            color: '#6a737d',
                            borderLeft: '0.25em solid #dfe2e5',
                            marginTop: 0,
                            marginBottom: '16px'
                          }
                        }
                      },
                      code: {
                        component: 'code',
                        props: {
                          style: {
                            backgroundColor: 'rgba(27,31,35,0.05)',
                            borderRadius: '3px',
                            fontSize: '85%',
                            padding: '0.2em 0.4em',
                            fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                            color: '#24292e'
                          }
                        }
                      },
                      pre: {
                        component: 'pre',
                        props: {
                          style: {
                            backgroundColor: '#f6f8fa',
                            borderRadius: '3px',
                            padding: '16px',
                            overflow: 'auto',
                            fontSize: '85%',
                            lineHeight: 1.45,
                            marginTop: 0,
                            marginBottom: '16px',
                            fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace'
                          }
                        }
                      },
                      strong: {
                        component: 'strong',
                        props: {
                          style: {
                            fontWeight: 600,
                            color: '#24292e'
                          }
                        }
                      },
                      b: {
                        component: 'b',
                        props: {
                          style: {
                            fontWeight: 600,
                            color: '#24292e'
                          }
                        }
                      },
                      em: {
                        component: 'em',
                        props: {
                          style: {
                            fontStyle: 'italic'
                          }
                        }
                      },
                      table: {
                        component: 'table',
                        props: {
                          style: {
                            width: '100%',
                            borderCollapse: 'collapse',
                            marginTop: 0,
                            marginBottom: '16px',
                            fontSize: '14px'
                          }
                        }
                      },
                      thead: {
                        component: 'thead',
                        props: {
                          style: {
                            backgroundColor: '#f6f8fa'
                          }
                        }
                      },
                      th: {
                        component: 'th',
                        props: {
                          style: {
                            border: '1px solid #dfe2e5',
                            padding: '6px 13px',
                            fontWeight: 600,
                            textAlign: 'left',
                            color: '#24292e'
                          }
                        }
                      },
                      td: {
                        component: 'td',
                        props: {
                          style: {
                            border: '1px solid #dfe2e5',
                            padding: '6px 13px',
                            color: '#24292e'
                          }
                        }
                      },
                      img: {
                        component: 'img',
                        props: {
                          style: {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                            marginTop: 0,
                            marginBottom: '16px'
                          }
                        }
                      }
                    }
                  }}
                >
                  {post?.content_markdown?.replace(/^(\s*)-\s+\[[x\s]\]\s+/gm, '$1- ')}
                </Markdown>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col md:flex-row gap-4 border-t border-[#F2F4F7] pt-4">
          <Button
            className="h-11 flex-1"
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: '#D0D5DD',
              color: '#344054',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              padding: '4px 12px'
              // '&:hover': {
              //   borderColor: '#D0D5DD',
              //   backgroundColor: 'transparent',
              // }
            }}
          >
            Close
          </Button>
          <Button
            className="h-11 flex-1"
            variant="outlined"
            onClick={handleCopyMarkdown}
            disabled={isLoading || !post}
            sx={{
              borderColor: '#D0D5DD',
              color: '#344054',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              padding: '4px 12px'
              // '&:hover': {
              //   backgroundColor: '#0F0A3C',
              // }
            }}
          >
            {copiedMarkdown ? 'Copied!' : 'Copy Markdown'}
          </Button>
          <Button
            className="h-11 flex-1"
            variant="contained"
            onClick={handleCopyText}
            disabled={isLoading || !post}
            sx={{
              backgroundColor: '#0F0A3C',
              color: '#FFFFFF',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              padding: '4px 12px'
              // '&:hover': {
              //   backgroundColor: '#0F0A3C',
              // }
            }}
          >
            {copiedText ? 'Copied!' : 'Copy Text'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
export default ViewBlogPostDialog;
